import './fonts'
import { Auth0Provider } from '@auth0/auth0-react'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import App from './App'
import { useAuthConfig } from './hooks/useAuthConfig'
import useCustomCache from './hooks/useCustomCache'
import { useDevice } from './hooks/useDevice'
import './index.scss'
import reportWebVitals from './reportWebVitals'
import { store } from './store'
import { CssBaseline } from '@mui/material'
import './sentry'

// Ensure that React hooks are called inside a React component or a custom hook.
// Moving the call of `useAuthConfig` to a functional component.
const RootComponent: React.FC = () => {
  // eslint-disable-next-line prefer-const
  let { isHybrid, isAndroid } = useDevice()

  // When testing and building the app for Android, set isAndroid to true. For building and testing on iOS, comment out the line below
  // isAndroid = true

  const { scope, domain, clientId, auth0Api, callbackUri } = useAuthConfig(
    isHybrid,
    isAndroid,
  )

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const sessionStorageCache = useCustomCache(isHybrid)

  return (
    <React.StrictMode>
      <Auth0Provider
        domain={domain}
        clientId={clientId}
        // cache={sessionStorageCache}
        cacheLocation="localstorage"
        // Refresh tokens must be enabled on WEB too, not only the native app. Without them, web
        // (especially mobile web) renews the session via silent iframe auth, which modern browsers
        // block (Safari/Chrome ITP + third-party-cookie blocking, and in-app WebViews). When that
        // renewal fails, isAuthenticated flips to false, AuthGuard bounces the user to
        // /unauthorized, and a legitimately signed-in user is dumped to "Unauthorized Access"
        // mid-session — a real conversion/retention killer for the global mobile-web audience.
        // Refresh-token rotation renews without third-party cookies. The Auth0 app already allows
        // offline access (the native app depends on it), so this needs no tenant change. On web,
        // keep the iframe fallback as a safety net; the native app has no iframe, so no fallback.
        useRefreshTokens
        useRefreshTokensFallback={!isHybrid}
        authorizationParams={{
          scope,
          audience: auth0Api,
          redirect_uri: callbackUri,
        }}
      >
        <Provider store={store}>
          <CssBaseline enableColorScheme />
          <App />
        </Provider>
      </Auth0Provider>
    </React.StrictMode>
  )
}

// Render the component to the root element.
const root = createRoot(document.getElementById('root') as HTMLElement)
root.render(<RootComponent />)

// Optional: Service worker registration for PWA
// import * as serviceWorkerRegistration from './serviceWorkerRegistration';
// serviceWorkerRegistration.unregister();

// Performance measurement
reportWebVitals()
